import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, FileText, Clock, CheckCircle, XCircle, AlertCircle, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow, parseISO } from 'date-fns';

// Mock data for simulation
const MOCK_QUOTES = [
  { id: '1', course_name: 'First Aid at Work', provider_name: 'SafetyFirst Ltd', status: 'pending', created_date: new Date(Date.now() - 86400000).toISOString(), attendee_count: 5 },
  { id: '2', course_name: 'Fire Warden Training', provider_name: 'FireSafe Training', status: 'quoted', created_date: new Date(Date.now() - 172800000).toISOString(), attendee_count: 3, quote_amount: 450 },
  { id: '3', course_name: 'Manual Handling', provider_name: 'Health Pro Ltd', status: 'pending', created_date: new Date(Date.now() - 259200000).toISOString(), attendee_count: 12 },
  { id: '4', course_name: 'COSHH Awareness', provider_name: null, status: 'pending', created_date: new Date(Date.now() - 345600000).toISOString(), attendee_count: 8 },
];

const getStatusConfig = (status) => {
  const configs = {
    pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
    quoted: { label: 'Quoted', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: FileText },
    accepted: { label: 'Accepted', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
    declined: { label: 'Declined', color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
  };
  return configs[status] || configs.pending;
};

export function TrainingQuotesTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setQuotes(MOCK_QUOTES);
      setLoading(false);
      return;
    }

    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadQuotes = async () => {
      try {
        setLoading(true);

        const { data, error } = await supabase
          .from('training_quotes')
          .select(`
            id,
            course_name,
            provider_name,
            status,
            created_date,
            attendee_count,
            quote_amount
          `)
          .eq('company_id', company.id)
          .in('status', ['pending', 'quoted'])
          .order('created_date', { ascending: false })
          .limit(10);

        if (error) throw error;
        setQuotes(data || []);
      } catch (error) {
        console.error('Failed to load quotes:', error);
      } finally {
        setLoading(false);
      }
    };

    loadQuotes();
  }, [company?.id, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
      </div>
    );
  }

  if (quotes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-gray-100 p-3 rounded-full mb-3">
          <FileText className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-900">No pending quotes</p>
        <p className="text-xs text-gray-500 mt-1">Request training quotes</p>
      </div>
    );
  }

  const pendingCount = quotes.filter(q => q.status === 'pending').length;
  const quotedCount = quotes.filter(q => q.status === 'quoted').length;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b shrink-0">
        <div className="flex items-center gap-2">
          <Badge className="bg-purple-500 text-white">{quotes.length}</Badge>
          <span className="text-xs text-gray-600">active quotes</span>
        </div>
        {quotedCount > 0 && (
          <Badge className="bg-blue-100 text-blue-800 text-[10px]">
            {quotedCount} ready to review
          </Badge>
        )}
      </div>

      {/* Quotes list */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="space-y-2 pr-2">
          {quotes.map((quote) => {
            const statusConfig = getStatusConfig(quote.status);
            const StatusIcon = statusConfig.icon;

            return (
              <div
                key={quote.id}
                onClick={() => navigate(`/bookings`)}
                className="p-2 rounded-lg border bg-white hover:shadow-md cursor-pointer transition-all"
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">
                      {quote.course_name}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {quote.provider_name ? (
                        <>
                          <Building2 className="h-2.5 w-2.5 text-gray-400" />
                          <span className="text-[10px] text-gray-600 truncate">
                            {quote.provider_name}
                          </span>
                        </>
                      ) : (
                        <span className="text-[10px] text-gray-500 italic">
                          Awaiting provider
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <Badge className={cn("text-[10px] px-1.5 py-0", statusConfig.color)}>
                        <StatusIcon className="h-2.5 w-2.5 mr-0.5" />
                        {statusConfig.label}
                      </Badge>
                      <span className="text-[10px] text-gray-500">
                        {quote.attendee_count} attendee{quote.attendee_count !== 1 ? 's' : ''}
                      </span>
                      {quote.quote_amount && (
                        <span className="text-[10px] font-medium text-green-600">
                          £{quote.quote_amount.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-[10px] text-gray-400 shrink-0">
                    {formatDistanceToNow(parseISO(quote.created_date), { addSuffix: true })}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Footer */}
      <button
        onClick={() => navigate('/bookings')}
        className="mt-2 pt-2 border-t flex items-center justify-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium transition-colors shrink-0"
      >
        <FileText className="h-3.5 w-3.5" />
        <span>Manage Quotes</span>
      </button>
    </div>
  );
}
